/**
 * AdminMessageList Component - List view for engagement messages
 *
 * Features:
 * - Status filter tabs (All, Draft, Published, Archived)
 * - Table with message details and actions
 * - Create new message button
 */

import React, { useState, useEffect, useCallback } from 'react';
import * as adminMessagesApi from '../../../api/adminMessages';
import { EXPLAINER_MESSAGE_TYPE } from '../../../utils/engagementShow';
import ConfirmModal from '../../Common/ConfirmModal';
import { useConfirm } from '../../Common/useConfirm';
import './AdminMessageList.css';

const STATUS_TABS = [
  { key: null, label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'published', label: 'Published' },
  { key: 'paused', label: 'Paused' },
  { key: 'archived', label: 'Archived' },
];

function AdminMessageList({ onEdit, onAnalytics, onCreate }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });
  const [errorMessage, setErrorMessage] = useState(null);
  const { confirm, modalProps } = useConfirm();

  const loadMessages = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminMessagesApi.listMessages({
        status: statusFilter,
        limit: pagination.limit,
        offset: pagination.offset,
      });
      setMessages(result.data || []);
      setPagination(prev => ({ ...prev, total: result.pagination?.total || 0 }));
    } catch (err) {
      setError(err.message || 'Failed to load messages');
      console.error('Failed to load messages:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, pagination.limit, pagination.offset]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const handlePublish = async (id) => {
    const ok = await confirm({ title: 'Publish message?', message: 'Publish this message? It will be shown to users.', confirmLabel: 'Publish' });
    if (!ok) return;
    try {
      await adminMessagesApi.publishMessage(id);
      loadMessages();
    } catch (err) {
      setErrorMessage('Failed to publish: ' + (err.message || 'Unknown error'));
    }
  };

  const handleArchive = async (id) => {
    const ok = await confirm({ title: 'Archive message?', message: 'Archive this message? It will no longer be shown to users.', confirmLabel: 'Archive', destructive: true });
    if (!ok) return;
    try {
      await adminMessagesApi.archiveMessage(id);
      loadMessages();
    } catch (err) {
      setErrorMessage('Failed to archive: ' + (err.message || 'Unknown error'));
    }
  };

  const handlePause = async (id) => {
    const ok = await confirm({ title: 'Pause message?', message: 'Pause this message? It will be temporarily hidden from users.', confirmLabel: 'Pause' });
    if (!ok) return;
    try {
      await adminMessagesApi.pauseMessage(id);
      loadMessages();
    } catch (err) {
      setErrorMessage('Failed to pause: ' + (err.message || 'Unknown error'));
    }
  };

  const handleResume = async (id) => {
    const ok = await confirm({ title: 'Resume message?', message: 'Resume this message? It will be shown to users again.', confirmLabel: 'Resume' });
    if (!ok) return;
    try {
      await adminMessagesApi.resumeMessage(id);
      loadMessages();
    } catch (err) {
      setErrorMessage('Failed to resume: ' + (err.message || 'Unknown error'));
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'draft': return 'status-badge--draft';
      case 'published': return 'status-badge--published';
      case 'paused': return 'status-badge--paused';
      case 'archived': return 'status-badge--archived';
      default: return '';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="admin-message-list">
      <div className="admin-message-list__header">
        <div className="admin-message-list__tabs">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.key || 'all'}
              className={`tab-button ${statusFilter === tab.key ? 'tab-button--active' : ''}`}
              onClick={() => {
                setStatusFilter(tab.key);
                setPagination(prev => ({ ...prev, offset: 0 }));
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button className="btn-primary" onClick={onCreate}>
          + New Message
        </button>
      </div>

      {error && (
        <div className="admin-message-list__error">
          {error}
          <button onClick={loadMessages}>Retry</button>
        </div>
      )}

      {loading ? (
        <div className="admin-message-list__loading">Loading messages...</div>
      ) : messages.length === 0 ? (
        <div className="admin-message-list__empty">
          <p>No messages found</p>
          <button className="btn-primary" onClick={onCreate}>
            Create your first message
          </button>
        </div>
      ) : (
        <table className="admin-message-list__table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Type</th>
              <th>Status</th>
              <th>Priority</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {messages.map(msg => (
              <tr key={msg.id}>
                <td className="title-cell">
                  <span className="message-title">{msg.title}</span>
                  {msg.target_new_users_only && (
                    <span className="targeting-badge">New users</span>
                  )}
                </td>
                <td>{msg.message_type}</td>
                <td>
                  <span className={`status-badge ${getStatusBadgeClass(msg.status)}`}>
                    {msg.status}
                  </span>
                </td>
                <td>{msg.priority}</td>
                <td>{formatDate(msg.created_at)}</td>
                <td className="actions-cell">
                  {msg.status === 'draft' && (
                    <>
                      <button className="btn-small" onClick={() => onEdit(msg)}>
                        Edit
                      </button>
                      <button className="btn-small btn-success" onClick={() => handlePublish(msg.id)}>
                        Publish
                      </button>
                    </>
                  )}
                  {msg.message_type === EXPLAINER_MESSAGE_TYPE
                    && (msg.status === 'published' || msg.status === 'scheduled') && (
                    <button
                      type="button"
                      className="btn-small"
                      onClick={() => { window.location.assign(`/?explainer=${msg.id}`); }}
                    >
                      Open explainer
                    </button>
                  )}
                  {msg.status === 'published' && (
                    <>
                      <button className="btn-small btn-warning" onClick={() => handlePause(msg.id)}>
                        Pause
                      </button>
                      <button className="btn-small" onClick={() => handleArchive(msg.id)}>
                        Archive
                      </button>
                    </>
                  )}
                  {msg.status === 'paused' && (
                    <>
                      <button className="btn-small btn-success" onClick={() => handleResume(msg.id)}>
                        Resume
                      </button>
                      <button className="btn-small" onClick={() => handleArchive(msg.id)}>
                        Archive
                      </button>
                    </>
                  )}
                  <button className="btn-small btn-ghost" onClick={() => onAnalytics(msg)}>
                    Analytics
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pagination.total > pagination.limit && (
        <div className="admin-message-list__pagination">
          <button
            disabled={pagination.offset === 0}
            onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset - prev.limit }))}
          >
            Previous
          </button>
          <span>
            {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
          </span>
          <button
            disabled={pagination.offset + pagination.limit >= pagination.total}
            onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
          >
            Next
          </button>
        </div>
      )}
      <ConfirmModal {...modalProps} />
      {errorMessage && (
        <div className="nav-inline-error" role="alert">
          {errorMessage}
          <button className="nav-inline-error__dismiss" onClick={() => setErrorMessage(null)}>×</button>
        </div>
      )}
    </div>
  );
}

export default AdminMessageList;
